/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useMemo, useRef } from "react";
import { format } from "date-fns";
import FilterBar from "../../../../../components/filter/filterBar";
import FilterItem from "../../../../../components/filter/filterItem";
import CompanyFilter from "../../../../../components/filters/CompanyFilter";
import { useNitroStore } from "../../../../../store/Store";
import { useNitroCAStore } from "../../../../../store/CompetitiveAnalysisStore";
import LocationExpandedFilter from "../../../../../components/filters/LocationExpandedFilter";
import MonthPicker from "../../../../../components/MonthPicker";

const getWhenDisplayValue = (
  monthValue: string,
  splitMonthKeys: string[],
): string => {
  if (!monthValue) {
    return "";
  }

  const monthDate = new Date(`${monthValue} 12:00`);
  if (Number.isNaN(monthDate.getTime())) {
    return "";
  }

  const monthKey = monthValue.slice(0, 7);
  if (splitMonthKeys.includes(monthKey)) {
    const day = Number(monthValue.slice(8, 10));
    const half = day >= 16 ? "2H" : "1H";
    return `${format(monthDate, "MMM")} ${half} ${format(monthDate, "yyyy")}`;
  }

  return format(monthDate, "MMM yyyy");
};

const FilterObj = ({
  currentField,
  setCurrentField,
  tempParty,
  setTempParty,
  tempCity,
  setTempCity,
  tempState,
  setTempState,
  tempLocation,
  setTempLocation,
  tempMonth,
  setTempMonth,
  setTempMode,
  hasChange,
  disabled,
  onClearWhoWhere,
}: {
  currentField: string | null;
  setCurrentField: (value: string | null) => void;
  tempParty: string;
  setTempParty: (value: string) => void;
  tempCity: string;
  setTempCity: (value: string) => void;
  tempState: string;
  setTempState: (value: string) => void;
  tempLocation: string;
  setTempLocation: (value: string) => void;
  tempMonth: string;
  setTempMonth: (value: string) => void;
  setTempMode: (value: string) => void;
  hasChange: boolean;
  disabled: boolean;
  onClearWhoWhere?: () => void;
}) => {
  const [
    hideAllPanels,
    isLoading,
    favoriteOriginsByUser,
    product,
    originManagerLiveSplitMonthKeysByProduct,
    triggerRefetchSurroundingLocations,
  ] = useNitroStore((state) => [
      state.hideAllPanels,
      state.isLoading,
      state.favoriteOriginsByUser,
      state.product,
      state.originManagerLiveSplitMonthKeysByProduct,
      state.triggerRefetchSurroundingLocations,
    ]);
  const [setDisplayInboundModeTogglePanel, triggerRefetchMultiLocation] =
    useNitroCAStore((state) => [
      state.setDisplayInboundModeTogglePanel,
      state.triggerRefetchMultiLocation,
    ]);

  const splitMonthKeys = useMemo(() => {
    const normalizedProduct = (product || "").toUpperCase();
    if (
      Object.prototype.hasOwnProperty.call(
        originManagerLiveSplitMonthKeysByProduct,
        normalizedProduct,
      )
    ) {
      return originManagerLiveSplitMonthKeysByProduct[normalizedProduct] ?? [];
    }

    const keys = new Set<string>();
    const originsForProduct =
      favoriteOriginsByUser.find(
        (origin) => origin.product.toUpperCase() === product.toUpperCase(),
      )?.favorites || [];

    originsForProduct.forEach((favorite) => {
      (favorite.months || []).forEach((monthData: any) => {
        const isSplitMonth =
          typeof monthData?.isSplit === "boolean"
            ? monthData.isSplit
            : (monthData?.periods || []).length > 1;

        if (!isSplitMonth) return;

        const monthKey =
          typeof monthData.month === "string"
            ? monthData.month.slice(0, 7)
            : `${monthData.month?.year}-${String(
                monthData.month?.month,
              ).padStart(2, "0")}`;

        if (monthKey && monthKey.length >= 7) {
          keys.add(monthKey);
        }
      });
    });

    return Array.from(keys);
  }, [
    favoriteOriginsByUser,
    originManagerLiveSplitMonthKeysByProduct,
    product,
  ]);

  const partyRef = useRef<any>();
  const locationRef = useRef<any>();
  return (
    <>
      {(currentField || hasChange) && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(15, 44, 82, 0.4)", // Opaque background when user has changed search but not hit search button
            zIndex: 25,
          }}
        />
      )}
      <FilterBar
        disabled={disabled}
        currentField={currentField}
        setCurrentField={(value) => {
          if (value === "company" || value === "location") {
            setDisplayInboundModeTogglePanel(false);
          }
          setCurrentField(value);
        }}
        isLoading={isLoading}
        clearWho={() => {
          onClearWhoWhere?.();
          setTempParty("");
          setTempMode("");
        }}
        clearWhere={() => {
          onClearWhoWhere?.();
          setTempCity("");
          setTempState("");
          setTempLocation("");
          setTempMode("");
        }}
      >
        <FilterItem
          property="company"
          label="WHO"
          placeholder="Search a Company"
          value={tempParty}
          type="filter"
          first
          className="companyFilterItem"
        >
          <CompanyFilter
            config={{
              autoFocus: true,
              customPaper: true,
              inputRef: partyRef,
            }}
            size="small"
            value={tempParty}
            setValue={(value) => {
              setTempParty(value || "");
              hideAllPanels();
            }}
            variant="standard"
          />
        </FilterItem>

        <FilterItem
          property="location"
          label="WHERE"
          placeholder="Search City or Facility"
          value={tempCity || tempState || tempLocation}
          type="filter"
          className="locationFilterItem"
        >
          <LocationExpandedFilter
            variant="standard"
            config={{
              autoFocus: true,
              customPaper: true,
              inputRef: locationRef,
            }}
            value={tempCity || tempState || tempLocation}
            setValue={(type, value) => {
              // Reselecting the same option doesn't change temp/filter state, so
              // force a refetch here since nothing else will trigger one.
              const isReselectingSameValue =
                (type === "city" && value === tempCity) ||
                (type === "facility" && value === tempLocation);

              if (type === "clear") {
                setTempCity("");
                setTempState("");
                setTempLocation("");
              }
              if (type === "city") {
                setTempCity(value);
                setTempState("");
                setTempLocation("");
                hideAllPanels();
              } else if (type === "facility") {
                setTempCity("");
                setTempState("");
                setTempLocation(value);
                hideAllPanels();
              } else {
                setTempCity("");
                setTempState("");
                setTempLocation("");
              }

              if (isReselectingSameValue) {
                triggerRefetchMultiLocation();
                triggerRefetchSurroundingLocations();
              }
            }}
          />
        </FilterItem>

        <FilterItem
          property="month"
          label="WHEN"
          placeholder="Select month"
          value={getWhenDisplayValue(tempMonth, splitMonthKeys)}
          type="popup"
          last
          className="whenFilterItem"
        >
          <div style={{ padding: 10 }}>
            <MonthPicker
              value={tempMonth}
              splitMonthKeys={splitMonthKeys}
              setValue={(newMonth: string) => {
                setTempMonth(newMonth);
              }}
            />
          </div>
        </FilterItem>
      </FilterBar>
    </>
  );
};

export default FilterObj;
